/**
 * Configuração central dos admins especializados de diligência.
 *
 * Centraliza labels, títulos, navegação e copy por especialidade.
 * Fonte da verdade única para toda a UX do admin especializado.
 */

import type { DiligenciaEspecialidade } from './diligenciaChecklists';

// =============================================================================
// TIPOS
// =============================================================================

export interface EspecialidadeConfig {
  /** Label curto da especialidade */
  label: string;
  /** Título do painel (header) */
  painelTitulo: string;
  /** Subtítulo descritivo */
  painelSubtitulo: string;
  /** Nome da fila de diligências */
  filaNome: string;
  /** Descrição da fila */
  filaDescricao: string;
  /** Ícone de cor para badges e indicadores */
  corAccent: string;
  /** Cor de fundo para badges */
  corBadgeBg: string;
  /** Cor de texto para badges */
  corBadgeText: string;
  /** Título do termo de uso */
  termoTitulo: string;
  /** Descrição do termo de uso */
  termoDescricao: string;
  /** Título da página de documentos de diligência */
  documentosTitulo: string;
  /** Subtítulo da página de documentos */
  documentosSubtitulo: string;
  /** Cards do dashboard contextual */
  dashboardCards: {
    pendentes: string;
    emAnalise: string;
    docPendente: string;
    recentes: string;
  };
}

// =============================================================================
// CONFIG POR ESPECIALIDADE
// =============================================================================

export const ESPECIALIDADE_CONFIG: Record<DiligenciaEspecialidade, EspecialidadeConfig> = {
  juridico: {
    label: 'Jurídico',
    painelTitulo: 'Painel Jurídico',
    painelSubtitulo: 'Análise jurídica de diligências',
    filaNome: 'Fila Jurídica',
    filaDescricao: 'Diligências na etapa de viabilidade jurídica',
    documentosTitulo: 'Documentos Jurídicos',
    documentosSubtitulo: 'Documentos recebidos do incorporador para análise jurídica',
    corAccent: 'text-indigo-600',
    corBadgeBg: 'bg-indigo-50',
    corBadgeText: 'text-indigo-700',
    termoTitulo: 'Termo de Acesso — Equipe Jurídica',
    termoDescricao:
      'Seu acesso é restrito às diligências, documentos e interações necessários para a análise jurídica. As informações acessadas são confidenciais e de uso exclusivo para fins de viabilidade jurídica.',
    dashboardCards: {
      pendentes: 'Pendentes de análise jurídica',
      emAnalise: 'Em análise jurídica',
      docPendente: 'Com pendência documental',
      recentes: 'Atualizadas recentemente',
    },
  },

  tecnica_economica: {
    label: 'Técnica/Econômica',
    painelTitulo: 'Painel Técnica/Econômica',
    painelSubtitulo: 'Análise técnica e econômica de diligências',
    filaNome: 'Fila Técnica/Econômica',
    filaDescricao: 'Diligências na etapa de viabilidade técnica e econômica',
    documentosTitulo: 'Documentos Técnicos',
    documentosSubtitulo: 'Documentos recebidos do incorporador para análise técnica e econômica',
    corAccent: 'text-emerald-600',
    corBadgeBg: 'bg-emerald-50',
    corBadgeText: 'text-emerald-700',
    termoTitulo: 'Termo de Acesso — Equipe Técnica/Econômica',
    termoDescricao:
      'Seu acesso é restrito às diligências, documentos e interações necessários para a análise técnica e econômica. As informações acessadas são confidenciais e de uso exclusivo para fins de viabilidade.',
    dashboardCards: {
      pendentes: 'Pendentes de análise técnica',
      emAnalise: 'Em análise técnica/econômica',
      docPendente: 'Com pendência documental',
      recentes: 'Atualizadas recentemente',
    },
  },

  contabil: {
    label: 'Contábil',
    painelTitulo: 'Painel Contábil',
    painelSubtitulo: 'Análise contábil de diligências',
    filaNome: 'Fila Contábil',
    filaDescricao: 'Diligências na etapa de viabilidade contábil',
    documentosTitulo: 'Documentos Contábeis',
    documentosSubtitulo: 'Documentos recebidos do incorporador para análise contábil',
    corAccent: 'text-amber-600',
    corBadgeBg: 'bg-amber-50',
    corBadgeText: 'text-amber-700',
    termoTitulo: 'Termo de Acesso — Equipe Contábil',
    termoDescricao:
      'Seu acesso é restrito às diligências, documentos e interações necessários para a análise contábil. As informações acessadas são confidenciais e de uso exclusivo para fins de viabilidade.',
    dashboardCards: {
      pendentes: 'Pendentes de análise contábil',
      emAnalise: 'Em análise contábil',
      docPendente: 'Com pendência documental',
      recentes: 'Atualizadas recentemente',
    },
  },
};

/**
 * Retorna a config da especialidade ou null se for admin principal.
 */
export function getEspecialidadeConfig(
  especialidade: DiligenciaEspecialidade | null | undefined
): EspecialidadeConfig | null {
  if (!especialidade) return null;
  return ESPECIALIDADE_CONFIG[especialidade] ?? null;
}
